// imports
import React from 'react';
import { Link, useLocation } from '@react-router';
import { pxToRem } from '@withjoy/joykit/theme';
import { JoyKitThemeProvider } from '@withjoy/joykit';

// local imports
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCarbonIframeLinkProps } from '@shared/hooks/useCarbonIframeLinkProps';
import { ReactComponent as JoyLogo } from '@assets/joy-logo.svg';
import { TravelCurrencySelector } from '@shared/components/TravelCurrencySelector';
import { TopBarContainer, LogoContainer, CurrencySelectorContainer } from './EventTopBar.styles';
import { TelemetryProvider, useEventTopBarTelemetry } from './EventTopBar.telemetry';

interface EventTopBarProps
  extends Readonly<{
    eventHandle: string;
  }> {}

const EventTopBarContent: React.FC<EventTopBarProps> = ({ eventHandle }) => {
  const location = useLocation();
  const [isCurrencySelectorEnabled] = useIsFeatureEnabled('travelGuestCurrencySwitch');
  const telemetry = useEventTopBarTelemetry();

  const handleLogoClick = useEventCallback(() => {
    telemetry.logoClicked();
  });
  const carbonIframeLinkProps = useCarbonIframeLinkProps();

  // Events with no guestsite resolve eventHandle to "stays", which 404s at `/stays`. Redirect to `/stays/search`
  // (the guest-site page that /travel also redirects to, per TRAV-2583).
  const logoTarget = eventHandle === 'stays' ? `/stays/search${location.search}` : `/${eventHandle}${location.search}`;

  return (
    <TopBarContainer>
      <LogoContainer
        as={Link}
        to={logoTarget}
        onClick={handleLogoClick}
        {...carbonIframeLinkProps}
      >
        <JoyLogo
          width={pxToRem(48)}
          height={pxToRem(30)}
          color="mono14"
        />
      </LogoContainer>

      <JoyKitThemeProvider>
        <CurrencySelectorContainer>
          <TravelCurrencySelector isEnabled={isCurrencySelectorEnabled} />
        </CurrencySelectorContainer>
      </JoyKitThemeProvider>
    </TopBarContainer>
  );
};

export const EventTopBar: React.FC<EventTopBarProps> = ({ eventHandle }) => {
  return (
    <TelemetryProvider>
      <EventTopBarContent eventHandle={eventHandle} />
    </TelemetryProvider>
  );
};
